import { GARDEN_OBJECT_TYPES } from '../../../constants/gardenObjectTypes';

/**
 * Overlap Detection Utilities for PlantCircle objects
 *
 * Three-state overlap detection:
 * - 'critical': Inner rings overlap (physical collision — no space to grow at all)
 * - 'warning':  Outer rings overlap (plants can't reach full size)
 * - 'none':     No overlap
 *
 * Growth-Type-Matrix: Tree + understory plants (perennial, herb, bulb, …)
 * are canopy-compatible — warning is downgraded to none.
 *
 * Design decisions:
 * - Warning only (non-blocking) - users can place overlapping plants
 * - Real-time updates during drag/move operations
 * - Three-state color feedback: Emerald (none), Amber (warning), Red (critical)
 */

// --- Growth-Type-Matrix ---------------------------------------------------

const CANOPY_TYPES = new Set(['tree']);
const UNDERSTORY_TYPES = new Set([
  'perennial', 'herb', 'bulb', 'annual',
  'grass', 'lawn', 'vegetable',
]);

/**
 * Check if two plants are canopy-compatible (tree + understory).
 * When compatible, a 'warning' overlap can be downgraded to 'none'.
 *
 * @param {object} plant1 - PlantCircle
 * @param {object} plant2 - PlantCircle
 * @returns {boolean}
 */
export function isCanopyCompatible(plant1, plant2) {
  const type1 = (
    plant1.plantProperties?.properties?.care_growth_type
    ?? plant1.plantProperties?.properties?.growth_type
    ?? ''
  ).toLowerCase();
  const type2 = (
    plant2.plantProperties?.properties?.care_growth_type
    ?? plant2.plantProperties?.properties?.growth_type
    ?? ''
  ).toLowerCase();

  if (!type1 || !type2) return false;

  return (CANOPY_TYPES.has(type1) && UNDERSTORY_TYPES.has(type2))
      || (CANOPY_TYPES.has(type2) && UNDERSTORY_TYPES.has(type1));
}

// --- Helpers ---------------------------------------------------------------

function getInnerRadius(plant) {
  if (typeof plant.getInnerRadius === 'function') return plant.getInnerRadius();
  // Fallback: treat single radius as inner
  if (typeof plant.getRadius === 'function') return plant.getRadius();
  return (plant.spacingCm || 60) / 2;
}

function getOuterRadius(plant) {
  if (typeof plant.getOuterRadius === 'function') return plant.getOuterRadius();
  if (typeof plant.getRadius === 'function') return plant.getRadius();
  return (plant.spacingCm || 60) / 2;
}

function distanceBetween(obj1, obj2) {
  const dx = obj1.left - obj2.left;
  const dy = obj1.top - obj2.top;
  return Math.sqrt(dx * dx + dy * dy);
}

// --- Core overlap checks ---------------------------------------------------

/**
 * Determine the overlap state between two plants.
 *
 * @param {object} plant1 - PlantCircle
 * @param {object} plant2 - PlantCircle
 * @returns {'none'|'warning'|'critical'}
 */
export function checkPairOverlapState(plant1, plant2) {
  // Canopy-compatible pairs (tree + understory) are always allowed at any distance.
  // A tree's inner ring represents its full crown — small plants can grow under it.
  if (isCanopyCompatible(plant1, plant2)) return 'none';

  const distance = distanceBetween(plant1, plant2);
  const innerSum = getInnerRadius(plant1) + getInnerRadius(plant2);
  const outerSum = getOuterRadius(plant1) + getOuterRadius(plant2);

  if (distance < innerSum) return 'critical';
  if (distance < outerSum) return 'warning';
  return 'none';
}

/**
 * Legacy binary check — kept for backward compatibility.
 * Returns true when state is 'warning' or 'critical'.
 */
export function checkCircleIntersection(circle1, circle2) {
  return checkPairOverlapState(circle1, circle2) !== 'none';
}

/**
 * Get all PlantCircle objects from canvas
 *
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @returns {Array<PlantCircle>} Array of PlantCircle objects
 *
 * @example
 * const plants = getAllPlantCircles(canvas);
 * console.log(`Found ${plants.length} plants on canvas`);
 */
export function getAllPlantCircles(canvas) {
  if (!canvas) {
    return [];
  }

  const objects = canvas.getObjects();
  return objects.filter(obj => obj.objectType === GARDEN_OBJECT_TYPES.PLANT);
}

/**
 * Detect all overlapping plant pairs on canvas
 *
 * Returns array of overlap pairs: [[plant1, plant2], [plant3, plant4], ...]
 * Uses O(n²) comparison - optimized version available for large datasets
 *
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @returns {Array<Array<PlantCircle>>} Array of overlapping plant pairs
 *
 * @example
 * const overlaps = detectAllOverlaps(canvas);
 * console.log(`Found ${overlaps.length} overlapping pairs`);
 * overlaps.forEach(([plant1, plant2]) => {
 *   console.log(`${plant1.plantName} overlaps with ${plant2.plantName}`);
 * });
 */
export function detectAllOverlaps(canvas) {
  const plants = getAllPlantCircles(canvas);
  const overlaps = [];

  // Compare each plant with every other plant (O(n²))
  for (let i = 0; i < plants.length; i++) {
    for (let j = i + 1; j < plants.length; j++) {
      if (checkCircleIntersection(plants[i], plants[j])) {
        overlaps.push([plants[i], plants[j]]);
      }
    }
  }

  return overlaps;
}

/**
 * Update three-state overlap for every plant on canvas.
 *
 * For each plant the worst state across all pairwise checks wins:
 *   critical > warning > none
 *
 * @param {fabric.Canvas} canvas
 * @returns {number} Number of plants with state !== 'none'
 */
export function updateOverlapStates(canvas) {
  const plants = getAllPlantCircles(canvas);

  // Collect worst state per plant
  const STATE_RANK = { none: 0, warning: 1, critical: 2 };
  const worstStates = new Map(plants.map(p => [p, 'none']));

  for (let i = 0; i < plants.length; i++) {
    for (let j = i + 1; j < plants.length; j++) {
      const state = checkPairOverlapState(plants[i], plants[j]);
      if (state === 'none') continue;

      for (const p of [plants[i], plants[j]]) {
        const cur = worstStates.get(p);
        if (STATE_RANK[state] > STATE_RANK[cur]) {
          worstStates.set(p, state);
        }
      }
    }
  }

  // Apply states
  let affectedCount = 0;
  worstStates.forEach((state, plant) => {
    if (typeof plant.setOverlapState === 'function') {
      plant.setOverlapState(state);
    } else if (typeof plant.setOverlapping === 'function') {
      // Legacy fallback
      plant.setOverlapping(state !== 'none');
    }
    if (state !== 'none') affectedCount++;
  });

  canvas.requestRenderAll();
  return affectedCount;
}

/**
 * Get overlapping plants for a specific plant
 *
 * Returns array of plants that overlap with the given plant.
 * Useful for showing specific overlap warnings in Properties Panel.
 *
 * @param {PlantCircle} targetPlant - Plant to check overlaps for
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @returns {Array<PlantCircle>} Array of plants overlapping with target
 *
 * @example
 * // Show overlap warning for selected plant
 * const selectedPlant = canvas.getActiveObject();
 * if (selectedPlant && selectedPlant.objectType === 'plant') {
 *   const overlappingPlants = getOverlappingPlantsFor(selectedPlant, canvas);
 *   if (overlappingPlants.length > 0) {
 *     const names = overlappingPlants.map(p => p.plantName).join(', ');
 *     console.warn(`Overlaps with: ${names}`);
 *   }
 * }
 */
export function getOverlappingPlantsFor(targetPlant, canvas) {
  const allPlants = getAllPlantCircles(canvas);
  const overlapping = [];

  allPlants.forEach(plant => {
    // Skip comparing plant with itself
    if (plant === targetPlant) {
      return;
    }

    if (checkCircleIntersection(targetPlant, plant)) {
      overlapping.push(plant);
    }
  });

  return overlapping;
}

/**
 * Check overlap state a plant would have at a given position (preview).
 *
 * Supports both the new two-radius signature and the legacy single-radius call.
 *
 * New:    wouldOverlapAtPosition(x, y, innerRadius, outerRadius, growthType, canvas, exclude)
 * Legacy: wouldOverlapAtPosition(x, y, radius, canvas, exclude)
 *
 * @returns {'none'|'warning'|'critical'}
 */
export function wouldOverlapAtPosition(x, y, innerRadiusOrRadius, outerRadiusOrCanvas, growthTypeOrExclude, canvasOrUndef, excludeOrUndef) {
  // Detect legacy call: 4th arg is a canvas object (has getObjects)
  let innerR, outerR, growthType, canvas, excludePlant;
  if (outerRadiusOrCanvas && typeof outerRadiusOrCanvas.getObjects === 'function') {
    // Legacy: (x, y, radius, canvas, exclude?)
    innerR = innerRadiusOrRadius;
    outerR = innerRadiusOrRadius;
    growthType = null;
    canvas = outerRadiusOrCanvas;
    excludePlant = growthTypeOrExclude || null;
  } else {
    // New: (x, y, innerRadius, outerRadius, growthType, canvas, exclude?)
    innerR = innerRadiusOrRadius;
    outerR = outerRadiusOrCanvas;
    growthType = growthTypeOrExclude || null;
    canvas = canvasOrUndef;
    excludePlant = excludeOrUndef || null;
  }

  const allPlants = getAllPlantCircles(canvas);
  const STATE_RANK = { none: 0, warning: 1, critical: 2 };
  let worst = 'none';

  for (const plant of allPlants) {
    if (plant === excludePlant) continue;

    const dx = x - plant.left;
    const dy = y - plant.top;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const plantInner = getInnerRadius(plant);
    const plantOuter = getOuterRadius(plant);

    // Check canopy compatibility first (tree + understory is always ok at any distance)
    const plantGrowthType = (
      plant.plantProperties?.properties?.care_growth_type
      ?? plant.plantProperties?.properties?.growth_type
      ?? ''
    ).toLowerCase();
    const compatible = growthType && plantGrowthType
      && ((CANOPY_TYPES.has(growthType) && UNDERSTORY_TYPES.has(plantGrowthType))
       || (CANOPY_TYPES.has(plantGrowthType) && UNDERSTORY_TYPES.has(growthType)));

    let state;
    if (compatible) {
      state = 'none';
    } else if (distance < innerR + plantInner) {
      state = 'critical';
    } else if (distance < outerR + plantOuter) {
      state = 'warning';
    } else {
      state = 'none';
    }

    if (STATE_RANK[state] > STATE_RANK[worst]) {
      worst = state;
    }
    if (worst === 'critical') break; // Can't get worse
  }

  return worst;
}

/**
 * Get nearest plants to a position (spatial query)
 *
 * Returns plants sorted by distance to given position.
 * Useful for performance optimization (only check nearby plants).
 *
 * @param {number} x - Center X position
 * @param {number} y - Center Y position
 * @param {number} maxDistance - Maximum distance to consider (pixels)
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @returns {Array<{plant: PlantCircle, distance: number}>} Plants sorted by distance
 *
 * @example
 * // Only check overlaps with nearby plants (performance optimization)
 * const nearbyPlants = getNearestPlants(x, y, 500, canvas);
 * nearbyPlants.forEach(({plant, distance}) => {
 *   console.log(`${plant.plantName} is ${distance}px away`);
 * });
 */
export function getNearestPlants(x, y, maxDistance, canvas) {
  const allPlants = getAllPlantCircles(canvas);
  const nearby = [];

  allPlants.forEach(plant => {
    const dx = x - plant.left;
    const dy = y - plant.top;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= maxDistance) {
      nearby.push({ plant, distance });
    }
  });

  // Sort by distance (closest first)
  nearby.sort((a, b) => a.distance - b.distance);

  return nearby;
}

/**
 * Debounced overlap detection for drag operations
 *
 * Returns a debounced version of updateOverlapStates for performance
 * during frequent updates (drag, scale, etc.).
 *
 * Debounce delay: 16ms (60fps) for smooth visual feedback
 *
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @param {number} delay - Debounce delay in milliseconds (default: 16ms)
 * @returns {function} Debounced update function
 *
 * @example
 * // Create debounced updater on component mount
 * const debouncedUpdate = createDebouncedOverlapDetection(canvas, 16);
 *
 * // Use during drag
 * canvas.on('object:moving', () => {
 *   debouncedUpdate();
 * });
 */
export function createDebouncedOverlapDetection(canvas, delay = 16) {
  let timeoutId = null;

  return function debouncedUpdate() {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      updateOverlapStates(canvas);
      timeoutId = null;
    }, delay);
  };
}

/**
 * Get a differentiated overlap message for a plant.
 *
 * @param {PlantCircle} plant
 * @param {Array<PlantCircle>} overlappingPlants
 * @returns {string|null} Message string, or null if no overlap
 */
export function getOverlapMessage(plant, overlappingPlants) {
  if (!overlappingPlants || overlappingPlants.length === 0) return null;

  const names = overlappingPlants.map(p => p.plantName).join(', ');
  const state = plant.overlapState || 'none';

  if (state === 'critical') {
    return `Pflanzabstand unterschritten! ${plant.plantName} hat physisch keinen Platz neben: ${names}`;
  }
  if (state === 'warning') {
    return `${plant.plantName} kann nicht voll auswachsen neben: ${names}`;
  }
  return null;
}

/**
 * Legacy wrapper — logs overlap warning to console.
 * @deprecated Use getOverlapMessage() instead
 */
export function showOverlapWarning(plant, overlappingPlants) {
  const msg = getOverlapMessage(plant, overlappingPlants);
  if (msg) {
    console.warn(msg);
  } else {
    // Fallback for legacy callers that don't set overlapState
    console.warn(
      `Pflanzabstand unterschritten! ${plant.plantName} überschneidet sich mit: ${overlappingPlants.map(p => p.plantName).join(', ')}`
    );
  }
}
